/**
 * View Animation Manager
 * 
 * Orchestrates choreographed view animations with strict sequencing.
 * 
 * Animation Sequence:
 * 0. Fade out dismissed/removed/moved-to-different-page views
 * 1. Shrink views getting fewer cells
 * 2. Move views to new positions
 * 3. Expand views getting more cells
 * 4. Fade in new/spawned views
 * 
 * This prevents visual chaos and overlapping during transitions.
 */

import type { ViewGeometry } from '../systems/process.js';
import { springValue } from './interpolators.js';
import type { AnimationProfile } from './profiles.js';
import type { BaseViewHTML } from '../systems/base-view-html.js';

/**
 * View change types
 */
export type ViewChangeType = 'dismiss' | 'remove' | 'move-page' | 'shrink' | 'move' | 'expand' | 'spawn' | 'fade-in';

/**
 * View change information
 */
export interface ViewChange {
  viewId: string;
  view: BaseViewHTML;
  previousGeometry: ViewGeometry;
  newGeometry: ViewGeometry;
  previousPage: number;
  newPage: number;
  previousSize: { cols: number; rows: number };
  newSize: { cols: number; rows: number };
  changeType: ViewChangeType;
}

/**
 * Animation state for a view
 */
interface ViewAnimationState {
  view: BaseViewHTML;
  geometry?: {
    current: ViewGeometry;
    target: ViewGeometry;
    velocity: { x: number; y: number; width: number; height: number };
    profile: AnimationProfile;
  };
  opacity?: {
    current: number;
    target: number;
    velocity: number;
    profile: AnimationProfile;
  };
  isActive: boolean;
  phase: number; // Which phase this animation belongs to (0-4)
}

/**
 * View Animation Manager
 * 
 * Manages choreographed view animations with strict sequencing.
 */
export class ViewAnimationManager {
  private animations: Map<string, ViewAnimationState> = new Map();
  private isAnimating: boolean = false;
  private currentPhase: number = -1;
  private phaseQueue: ViewChange[][] = [[], [], [], [], []]; // 5 phases

  /**
   * Animate view changes with choreographed sequencing
   */
  async animateViewChanges(changes: ViewChange[]): Promise<void> {
    if (changes.length === 0) return;

    // Cancel any ongoing animations
    this.cancelAll();

    // Group changes by phase
    this.phaseQueue = [[], [], [], [], []];
    
    for (const change of changes) {
      const phase = this.getPhaseForChange(change);
      this.phaseQueue[phase].push(change);
    }

    // Execute phases sequentially
    await this.executePhases();
  }

  /**
   * Get phase number for a change type
   */
  private getPhaseForChange(change: ViewChange): number {
    switch (change.changeType) {
      case 'dismiss':
      case 'remove':
      case 'move-page':
        return 0; // Phase 0: Fade out
      case 'shrink':
        return 1; // Phase 1: Shrink
      case 'move':
        return 2; // Phase 2: Move
      case 'expand':
        return 3; // Phase 3: Expand
      case 'spawn':
      case 'fade-in':
        return 4; // Phase 4: Fade in
      default:
        return 2; // Default to move
    }
  }

  /**
   * Execute animation phases sequentially
   */
  private async executePhases(): Promise<void> {
    this.isAnimating = true;

    // Phase 0: Fade out dismissed/removed/moved-to-different-page views
    if (this.phaseQueue[0].length > 0) {
      this.currentPhase = 0;
      await this.executePhase(0, this.phaseQueue[0]);
    }

    // Phase 1: Shrink views getting fewer cells
    if (this.phaseQueue[1].length > 0) {
      this.currentPhase = 1;
      await this.executePhase(1, this.phaseQueue[1]);
    }

    // Phase 2: Move views to new positions
    if (this.phaseQueue[2].length > 0) {
      this.currentPhase = 2;
      await this.executePhase(2, this.phaseQueue[2]);
    }

    // Phase 3: Expand views getting more cells
    if (this.phaseQueue[3].length > 0) {
      this.currentPhase = 3;
      await this.executePhase(3, this.phaseQueue[3]);
    }

    // Phase 4: Fade in new/spawned views
    if (this.phaseQueue[4].length > 0) {
      this.currentPhase = 4;
      await this.executePhase(4, this.phaseQueue[4]);
    }

    this.isAnimating = false;
    this.currentPhase = -1;
  }

  /**
   * Execute a single animation phase
   */
  private async executePhase(phase: number, changes: ViewChange[]): Promise<void> {
    // Start animations for all changes in this phase
    for (const change of changes) {
      this.startAnimation(change, phase);
    }

    // Wait for all animations in this phase to complete
    await this.waitForPhaseCompletion(phase);
  }

  /**
   * Start animation for a view change
   */
  private startAnimation(change: ViewChange, phase: number): void {
    const view = change.view;
    const previousGeometry = change.previousGeometry;
    const newGeometry = change.newGeometry;

    // Get animation profile based on phase
    const profile = this.getProfileForPhase(phase, change.changeType);

    // Initialize animation state
    const animationState: ViewAnimationState = {
      view,
      isActive: true,
      phase
    };

    // Set up geometry animation (for all phases except pure fade)
    // For spawn/fade-in: Don't animate from (0,0) - start at target position, only fade in
    if (change.changeType !== 'dismiss' && change.changeType !== 'remove' && change.changeType !== 'move-page') {
      // For spawn/fade-in: Start at target position (no position animation)
      if (change.changeType === 'spawn' || change.changeType === 'fade-in') {
        // Only animate size if it's changing, but start at target position
        animationState.geometry = {
          current: { ...newGeometry }, // Start at target position
          target: { ...newGeometry },
          velocity: { x: 0, y: 0, width: 0, height: 0 },
          profile
        };
      } else {
        // For shrink/move/expand: Animate from previous to new
        animationState.geometry = {
          current: { ...previousGeometry },
          target: { ...newGeometry },
          velocity: { x: 0, y: 0, width: 0, height: 0 },
          profile
        };
        
        // Play view move sound for phase 2 (position changes)
        if (phase === 2 && change.changeType === 'move') {
          const deltaX = (newGeometry.x || 0) - (previousGeometry.x || 0);
          const viewportWidth = window.innerWidth;
          // Calculate pan based on movement direction: left = negative, right = positive
          const pan = Math.max(-0.5, Math.min(0.5, (deltaX / viewportWidth) * 2));
        }
      }
    }

    // Set up opacity animation
    if (phase === 0) {
      // Phase 0: Fade out (1.0 → 0.0)
      animationState.opacity = {
        current: 1.0,
        target: 0.0,
        velocity: 0,
        profile: this.getFadeProfile('out')
      };
    } else if (phase === 4) {
      // Phase 4: Fade in (0.0 → 1.0)
      animationState.opacity = {
        current: 0.0,
        target: 1.0,
        velocity: 0,
        profile: this.getFadeProfile('in')
      };
    }

    // Store animation state
    this.animations.set(change.viewId, animationState);

    // Initialize view geometry to starting state
    if (animationState.geometry) {
      // For spawn/fade-in: Set to target position immediately (no position animation)
      // For other changes: Set to previous position (will animate to target)
      view.updateGeometry(animationState.geometry.current); // No animation, set directly
    } else {
      // No geometry animation - set to target immediately
      view.updateGeometry(newGeometry);
    }

    // Initialize view opacity to starting state
    if (animationState.opacity) {
      // For HTML views, opacity is handled via CSS, not geometry
      // We'll apply it directly to the element in the update loop
      view.markDirty(); // Trigger re-render with initial opacity
    }
  }

  /**
   * Get animation profile for a phase
   */
  private getProfileForPhase(phase: number, changeType: ViewChangeType): AnimationProfile {
    // Sharp animations for size/position (phases 1-3)
    // Use ease-out for truly sharp, non-bouncy animations
    if (phase >= 1 && phase <= 3) {
      return {
        type: 'easeInOut', // Ease-out for sharp, non-bouncy
        stiffness: 0.45,
        damping: 0.98,   // Very high damping = no bounce
        speed: 0.25,     // Faster speed for sharp feel
        label: 'Sharp'
      };
    }

    // Default spring profile
    return {
      type: 'spring',
      stiffness: 0.35,
      damping: 0.90,
      speed: 0.18,
      label: 'Smooth'
    };
  }

  /**
   * Get fade animation profile
   */
  private getFadeProfile(direction: 'in' | 'out'): AnimationProfile {
    return {
      type: 'spring',
      stiffness: 0.35, // Medium stiffness = smooth
      damping: 0.90,    // Medium damping = slight ease
      speed: 0.18,
      label: direction === 'in' ? 'Fade In' : 'Fade Out'
    };
  }

  /**
   * Wait for all animations in a phase to complete
   */
  private async waitForPhaseCompletion(phase: number): Promise<void> {
    return new Promise((resolve) => {
      const checkComplete = () => {
        // Check if all animations in this phase are complete
        const phaseAnimations = Array.from(this.animations.values()).filter(a => a.phase === phase);
        
        if (phaseAnimations.length === 0) {
          resolve();
          return;
        }

        const allComplete = phaseAnimations.every(anim => {
          if (!anim.isActive) return true;

          // Check geometry animation
          if (anim.geometry) {
            const { current, target, velocity } = anim.geometry;
            const dx = Math.abs(target.x - current.x);
            const dy = Math.abs(target.y - current.y);
            const dw = Math.abs((target.width ?? 0) - (current.width ?? 0));
            const dh = Math.abs((target.height ?? 0) - (current.height ?? 0));
            const vx = Math.abs(velocity.x);
            const vy = Math.abs(velocity.y);
            const vw = Math.abs(velocity.width);
            const vh = Math.abs(velocity.height);

            const precision = 0.1;
            const geometryComplete = dx < precision && dy < precision &&
                                    dw < precision && dh < precision &&
                                    vx < precision && vy < precision &&
                                    vw < precision && vh < precision;

            if (!geometryComplete) return false;
          }

          // Check opacity animation
          if (anim.opacity) {
            const { current, target, velocity } = anim.opacity;
            const da = Math.abs(target - current);
            const v = Math.abs(velocity);

            const precision = 0.01;
            const opacityComplete = da < precision && v < precision;

            if (!opacityComplete) return false;
          }

          return true;
        });

        if (allComplete) {
          // Mark animations as complete
          phaseAnimations.forEach(anim => {
            anim.isActive = false;
            // Snap to final values
            if (anim.geometry) {
              anim.geometry.current = { ...anim.geometry.target };
              anim.geometry.velocity = { x: 0, y: 0, width: 0, height: 0 };
            }
            if (anim.opacity) {
              anim.opacity.current = anim.opacity.target;
              anim.opacity.velocity = 0;
            }
          });
          resolve();
        } else {
          // Check again next frame
          requestAnimationFrame(checkComplete);
        }
      };

      checkComplete();
    });
  }

  /**
   * Update animation state (called from animation loop)
   */
  update(deltaTime: number): void {
    if (!this.isAnimating) return;

    for (const [viewId, anim] of this.animations.entries()) {
      if (!anim.isActive) continue;

      let needsUpdate = false;

      // Update geometry animation
      if (anim.geometry) {
        const { current, target, velocity, profile } = anim.geometry;

        // Check if position/size actually changed (for spawn, might already be at target)
        const positionChanged = current.x !== target.x || current.y !== target.y;
        const sizeChanged = current.width !== target.width || current.height !== target.height;

        // Use sharp ease-out for phases 1-3 (shrink, move, expand), spring for smooth (phase 4)
        if (profile.type === 'easeInOut' && (positionChanged || sizeChanged)) {
          // Sharp cubic ease-out interpolation - clean deceleration, no bounce
          // Cubic ease-out: 1 - (1-t)^3 for sharp, clean feel
          const speed = profile.speed || 0.25;
          
          if (positionChanged) {
            const dx = target.x - current.x;
            const dy = target.y - current.y;
            
            // Calculate remaining distance
            const distX = Math.abs(dx);
            const distY = Math.abs(dy);
            const maxDist = Math.max(distX, distY);
            
            if (maxDist > 0.1) {
              // Calculate step size with cubic ease-out
              // Progress = 1 - (1-t)^3 where t is normalized distance
              const normalizedDist = Math.min(1, maxDist / 200); // Normalize to 0-1
              const easedProgress = 1 - Math.pow(1 - normalizedDist, 3); // Cubic ease-out
              const stepSize = speed * (0.4 + 0.6 * easedProgress); // Faster at start, slower at end
              
              // Apply movement
              const moveX = dx * stepSize;
              const moveY = dy * stepSize;
              
              current.x += moveX;
              current.y += moveY;
              
              // Snap to target if close enough
              if (Math.abs(target.x - current.x) < 0.1) current.x = target.x;
              if (Math.abs(target.y - current.y) < 0.1) current.y = target.y;
            } else {
              // Close enough - snap to target
              current.x = target.x;
              current.y = target.y;
            }
          }
          
          if (sizeChanged && target.width !== undefined && target.height !== undefined) {
            const currentWidth = current.width ?? 0;
            const currentHeight = current.height ?? 0;
            const dw = target.width - currentWidth;
            const dh = target.height - currentHeight;
            
            // Calculate remaining distance
            const distW = Math.abs(dw);
            const distH = Math.abs(dh);
            const maxDist = Math.max(distW, distH);
            
            if (maxDist > 0.1) {
              // Calculate step size with cubic ease-out
              const normalizedDist = Math.min(1, maxDist / 200);
              const easedProgress = 1 - Math.pow(1 - normalizedDist, 3);
              const stepSize = speed * (0.4 + 0.6 * easedProgress);
              
              // Apply movement
              const moveW = dw * stepSize;
              const moveH = dh * stepSize;
              
              current.width = currentWidth + moveW;
              current.height = currentHeight + moveH;
              
              // Snap to target if close enough
              if (Math.abs(target.width - current.width) < 0.1) current.width = target.width;
              if (Math.abs(target.height - current.height) < 0.1) current.height = target.height;
            } else {
              // Close enough - snap to target
              current.width = target.width;
              current.height = target.height;
            }
          }
        } else {
          // Use spring physics for smooth animations (fade in/out)
          // Update X
          const xResult = springValue(current.x, velocity.x, target.x, profile);
          current.x = xResult.pos;
          velocity.x = xResult.vel;

          // Update Y
          const yResult = springValue(current.y, velocity.y, target.y, profile);
          current.y = yResult.pos;
          velocity.y = yResult.vel;

          // Update Width
          if (target.width !== undefined && current.width !== undefined) {
            const wResult = springValue(current.width, velocity.width, target.width, profile);
            current.width = wResult.pos;
            velocity.width = wResult.vel;
          }

          // Update Height
          if (target.height !== undefined && current.height !== undefined) {
            const hResult = springValue(current.height, velocity.height, target.height, profile);
            current.height = hResult.pos;
            velocity.height = hResult.vel;
          }
        }

        // Update view geometry
        anim.view.updateGeometry(current); // No animation, set directly
        needsUpdate = true;
      }

      // Update opacity animation
      if (anim.opacity) {
        const { current, target, velocity, profile } = anim.opacity;

        const result = springValue(current, velocity, target, profile);
        anim.opacity.current = result.pos;
        anim.opacity.velocity = result.vel;

        // Update view opacity - for HTML views, set on element style
        // Use updateGeometry to set opacity via CSS
        const geometry = anim.view.getGeometry();
        geometry.opacity = result.pos;
        anim.view.updateGeometry(geometry);
        needsUpdate = true;
      }

      if (needsUpdate) {
        anim.view.markDirty();
      }
    }
  }

  /**
   * Cancel animation for a specific view
   */
  cancel(viewId: string): void {
    const anim = this.animations.get(viewId);
    if (anim) {
      anim.isActive = false;
      this.animations.delete(viewId);
    }
  }

  /**
   * Cancel all animations
   */
  cancelAll(): void {
    for (const anim of this.animations.values()) {
      anim.isActive = false;
    }
    this.animations.clear();
    this.isAnimating = false;
    this.currentPhase = -1;
  }

  /**
   * Check if any animations are active
   */
  isActive(): boolean {
    return this.isAnimating || this.animations.size > 0;
  }

  /**
   * Get current phase
   */
  getCurrentPhase(): number {
    return this.currentPhase;
  }
}

// Singleton instance
export const viewAnimationManager = new ViewAnimationManager();

